import GlassmorphicModal from '../../../../component/ReusableComponents/GlassmorphicModal';
import React, { useState, useEffect } from 'react';

const AddProject = ({ onClose, onAddProject, editingProject }) => {
  const [form, setForm] = useState({
    name: '',
    description: '',
    vendor: '',
    vendorType: '',
    vendorEmail: '',
    vendorContact: '',
    startDate: '',
    endDate: '',
    status: '',
  });

  const [errors, setErrors] = useState({});
  const [isOpen, setIsOpen] = useState(true);

  const vendorTypes = [
    'Plumber',
    'Electrician',
    'Carpenter',
    'Contractor',
    'Painter',
    'Mason',
    'HVAC',
    'Landscaper',
    'Roofer',
    'Pest Control',
    'Cleaning Services',
  ];

  useEffect(() => {
    if (editingProject) {
      setForm({
        name: editingProject.name || '',
        description: editingProject.description || '',
        vendor: editingProject.vendor || '',
        vendorType: editingProject.vendorType || '',
        vendorEmail: editingProject.vendorEmail || '',
        vendorContact: editingProject.vendorContact || '',
        startDate: editingProject.startDate || '',
        endDate: editingProject.endDate || '',
        status: editingProject.status || '',
      });
    } else {
      setForm({
        name: '',
        description: '',
        vendor: '',
        vendorType: '',
        vendorEmail: '',
        vendorContact: '',
        startDate: '',
        endDate: '',
        status: '',
      });
    }
  }, [editingProject]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};

    // Required fields - only non-vendor fields
    const requiredFields = ['name', 'description', 'startDate', 'endDate', 'status'];
    requiredFields.forEach((field) => {
      if (!form[field].trim()) {
        newErrors[field] = 'This field is required';
      }
    });

    if (form.startDate && form.endDate && new Date(form.startDate) > new Date(form.endDate)) {
      newErrors.endDate = 'End date cannot be before start date';
    }

    // Validate email format only if email is provided
    if (form.vendorEmail && form.vendorEmail.trim() && !/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(form.vendorEmail)) {
      newErrors.vendorEmail = 'Invalid email format';
    }

    // Validate contact format only if contact is provided
    if (form.vendorContact && form.vendorContact.trim() && !/^\d{10}$/.test(form.vendorContact)) {
      newErrors.vendorContact = 'Contact must be 10 digits';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleClose = () => {
    setForm({
      name: '',
      description: '',
      vendor: '',
      vendorType: '',
      vendorEmail: '',
      vendorContact: '',
      startDate: '',
      endDate: '',
      status: '',
    });
    setErrors({});
    setIsOpen(false);
    onClose();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      const projectData = {
        id: editingProject?.id || Date.now(),
        name: form.name,
        description: form.description,
        vendor: form.vendor,
        vendorType: form.vendorType,
        vendorEmail: form.vendorEmail,
        vendorContact: form.vendorContact,
        startDate: form.startDate,
        endDate: form.endDate,
        status: form.status,
      };
      onAddProject(projectData);
    }
  };

  const modalFooter = (
    <div className="flex justify-end gap-3">
      <button
        type="button"
        className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
        onClick={handleClose}
      >
        Cancel
      </button>
      <button
        type="submit"
        form="project-form"
        className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
      >
        {editingProject ? 'Save Changes' : 'Submit'}
      </button>
    </div>
  );

  return (
    <GlassmorphicModal
      isOpen={isOpen}
      onClose={handleClose}
      title={editingProject ? 'Edit Project' : 'Add Project'}
      size="lg"
      footer={modalFooter}
    >
      <form id="project-form" onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Project Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="name"
              value={form.name}
              onChange={handleChange}
              className={`w-full rounded-md border ${errors.name ? "border-red-500" : "border-gray-300"} px-4 py-2 focus:outline-none focus:border-blue-400 focus:bg-blue-200/15 transition-colors`}
              autoComplete="off"
            />
            {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Vendor Name
            </label>
            <input
              type="text"
              name="vendor"
              value={form.vendor}
              onChange={handleChange}
              className={`w-full rounded-md border ${errors.vendor ? "border-red-500" : "border-gray-300"} px-4 py-2 focus:outline-none focus:border-blue-400 focus:bg-blue-200/15 transition-colors`}
              autoComplete="off"
            />
            {errors.vendor && <p className="text-red-500 text-xs mt-1">{errors.vendor}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Vendor Type
            </label>
            <select
              name="vendorType"
              value={form.vendorType}
              onChange={handleChange}
              className={`w-full rounded-md border ${errors.vendorType ? "border-red-500" : "border-gray-300"} px-4 py-2 focus:outline-none focus:border-blue-400 focus:bg-blue-200/15 transition-colors`}
            >
              <option value="">Select Vendor Type</option>
              {vendorTypes.map((type, index) => (
                <option key={index} value={type}>
                  {type}
                </option>
              ))}
            </select>
            {errors.vendorType && <p className="text-red-500 text-xs mt-1">{errors.vendorType}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Vendor Email
            </label>
            <input
              type="email"
              name="vendorEmail"
              value={form.vendorEmail}
              onChange={handleChange}
              className={`w-full rounded-md border ${errors.vendorEmail ? "border-red-500" : "border-gray-300"} px-4 py-2 focus:outline-none focus:border-blue-400 focus:bg-blue-200/15 transition-colors`}
              autoComplete="off"
            />
            {errors.vendorEmail && <p className="text-red-500 text-xs mt-1">{errors.vendorEmail}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Vendor Contact
            </label>
            <input
              type="text"
              name="vendorContact"
              value={form.vendorContact}
              onChange={handleChange}
              className={`w-full rounded-md border ${errors.vendorContact ? "border-red-500" : "border-gray-300"} px-4 py-2 focus:outline-none focus:border-blue-400 focus:bg-blue-200/15 transition-colors`}
              autoComplete="off"
            />
            {errors.vendorContact && <p className="text-red-500 text-xs mt-1">{errors.vendorContact}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status <span className="text-red-500">*</span>
            </label>
            <select
              name="status"
              value={form.status}
              onChange={handleChange}
              className={`w-full rounded-md border ${errors.status ? "border-red-500" : "border-gray-300"} px-4 py-2 focus:outline-none focus:border-blue-400 focus:bg-blue-200/15 transition-colors`}
            >
              <option value="">Select Status</option>
              <option value="To be Start">To be Start</option>
              <option value="In Progress">In Progress</option>
              <option value="Completed">Completed</option>
            </select>
            {errors.status && <p className="text-red-500 text-xs mt-1">{errors.status}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Start Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              name="startDate"
              value={form.startDate}
              onChange={handleChange}
              className={`w-full rounded-md border ${errors.startDate ? "border-red-500" : "border-gray-300"} px-4 py-2 focus:outline-none focus:border-blue-400 focus:bg-blue-200/15 transition-colors`}
            />
            {errors.startDate && <p className="text-red-500 text-xs mt-1">{errors.startDate}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              End Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              name="endDate"
              value={form.endDate}
              onChange={handleChange}
              className={`w-full rounded-md border ${errors.endDate ? "border-red-500" : "border-gray-300"} px-4 py-2 focus:outline-none focus:border-blue-400 focus:bg-blue-200/15 transition-colors`}
            />
            {errors.endDate && <p className="text-red-500 text-xs mt-1">{errors.endDate}</p>}
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description <span className="text-red-500">*</span>
            </label>
            <textarea
              name="description"
              value={form.description}
              onChange={handleChange}
              rows={4}
              className={`w-full rounded-md border ${errors.description ? "border-red-500" : "border-gray-300"} px-4 py-2 focus:outline-none focus:border-blue-400 focus:bg-blue-200/15 transition-colors resize-y`}
              autoComplete="off"
            />
            {errors.description && <p className="text-red-500 text-xs mt-1">{errors.description}</p>}
          </div>
        </div>
      </form>
    </GlassmorphicModal>
  );
};

export default AddProject;